import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getNotice,
  updateNotice,
  deleteNotice,
  type NoticeResponse,
  type UpdateNoticeRequest,
} from '../../endpoints/notices';
import AlertItem from '../../Components/AlertItem';
import dayjs from 'dayjs';

const NoticeManagementEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState(false);

  const {
    data: notice,
    isLoading,
    error,
  } = useApiGet<NoticeResponse>(['notice', id!], () => getNotice(id!), {
    enabled: !!id,
  });

  React.useEffect(() => {
    if (notice) {
      setTitle(notice.title ?? '');
      setDescription(notice.description ?? '');
      setLinkUrl(notice.link_url ?? '');
      setExpiryDate(
        notice.due_date ? dayjs(notice.due_date).format('YYYY-MM-DD') : ''
      );
    }
  }, [notice]);

  const { mutate: updateMutate, isPending: isSaving } = useApiSend(
    ({ id: noticeId, data }: { id: string; data: UpdateNoticeRequest }) =>
      updateNotice(noticeId, data),
    () => {
      setAlertState({
        severity: 'success',
        description: 'Notice updated.',
      });
    },
    (err: Error) => {
      setAlertState({
        severity: 'error',
        description:
          (err as any)?.response?.data?.error ||
          (err as any)?.message ||
          'Update failed.',
      });
    },
    [['notices', 'notice', id]]
  );

  const { mutate: deleteMutate, isPending: isDeleting } = useApiSend(
    (noticeId: string) => deleteNotice(noticeId),
    () => {
      setRemoveConfirm(false);
      setAlertState({
        severity: 'success',
        description:
          'Notice retired. All recipients have been marked as completed.',
      });
      navigate('/administration/notice-management');
    },
    (err: Error) => {
      setAlertState({
        severity: 'error',
        description:
          (err as any)?.response?.data?.error ||
          (err as any)?.message ||
          'Remove failed.',
      });
    },
    [['notices']]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    if (!title.trim()) {
      setAlertState({ severity: 'error', description: 'Title is required.' });
      return;
    }
    if (!description.trim()) {
      setAlertState({
        severity: 'error',
        description: 'Description is required.',
      });
      return;
    }
    updateMutate({
      id,
      data: {
        title: title.trim(),
        description: description.trim(),
        link_url: linkUrl.trim() || undefined,
        expiry_date: expiryDate || undefined,
      },
    });
  };

  const handleRemoveConfirm = () => {
    if (id) deleteMutate(id);
  };

  if (!id) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography color="error">Missing notice ID.</Typography>
        </Grid>
      </Grid>
    );
  }

  if (isLoading || (!notice && !error)) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (error || !notice) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography color="error">
            {(error as Error)?.message || 'Notice not found.'}
          </Typography>
          <Button
            sx={{ mt: 1 }}
            onClick={() => navigate('/administration/notice-management')}
          >
            Back to list
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Edit Notice
        </Typography>
      </Grid>
      {alertState && (
        <Grid item xs={12}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Link URL (optional)"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expiry date"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Recipients (read-only)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(notice.recipient_names && notice.recipient_names.length > 0
                    ? notice.recipient_names
                    : (notice.recipient_group_type_ids ?? [])
                  ).map((label, idx) => (
                    <Chip
                      key={`${label}-${idx}`}
                      size="small"
                      label={label}
                      variant="outlined"
                    />
                  ))}
                  {(!notice.recipient_names ||
                    notice.recipient_names.length === 0) &&
                    (!notice.recipient_group_type_ids ||
                      notice.recipient_group_type_ids.length === 0) && (
                      <Typography variant="body2" color="text.secondary">
                        No recipients
                      </Typography>
                    )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() =>
                      navigate('/administration/notice-management')
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} /> : null}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    type="button"
                    color="error"
                    variant="outlined"
                    onClick={() => setRemoveConfirm(true)}
                    disabled={isDeleting}
                  >
                    Retire notice
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
      {removeConfirm && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography gutterBottom>
              Retiring this notice will expire it and mark it as completed for
              all recipients. Continue?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleRemoveConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Retiring…' : 'Yes, retire'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setRemoveConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default NoticeManagementEdit;
